import { Typography, Box } from '@mui/material';
import * as yup from 'yup';
import Model from '../../models/model';
import strings from '../../Globalization';
import connectionService from '../../services/Connection';
import consts from '../../consts';
import util from '../../util';
import CommandServicesDataGrid from './BrokerServicesForm';

const { biConfigId, incubatorDeviceType, biConfigCommand } = consts;
const { showError } = util;
const onfileSelected = async (fieldName, type) => {
	const file = fieldName.value;
	let error;
	try {
		if (file && file.type === 'application/json') {
			const parsed = await new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = (e) => {
					const fileData = e.target.result;
					let json;
					try {
						json = JSON.parse(fileData);
					} catch (parseError) {
						error = { title: strings.Warning, content: strings.InvalidConfigMsg, level: 'warning' };
						reject(error);
					}
					if (fieldName.values.Command === consts.biConfigId) {
						const formData = new FormData();
						formData.append('action', 'otherAction');
						formData.append('otherAction', 'CompareCrcFile');
						formData.append('fileData', fileData);
						formData.append('fileName', file.name);
						fetch(connectionService.Command, { method: 'POST', body: formData })
							.then((res) => res.json())
							.then((responseJson) => {
								if (responseJson.success) {
									resolve(json);
								} else {
									error = { title: strings.ErrorTitle, content: strings.FileNameInvalid, level: 'error' };
									reject(error);
								}
							})
							.catch(() => {
								error = { title: strings.Warning, content: strings.FileNameInvalid, level: 'warning' };
								reject(error);
							});
					}
					else {
						resolve(json);
					}
				};
				reader.readAsText(file);
			});

			if (fieldName.values.Command === consts.biConfigId) {
				const paramsValid = parsed.BiNumber && parsed.BiName && parsed.BiCycle;
				if (paramsValid) {
					fieldName.formik.setFieldValue('ConfigSetting', JSON.stringify(parsed, null, 2));
				} else {
					error = { title: strings.ErrorTitle, content: strings.BiParameterMissing, level: 'error' };
				}
			} else if (fieldName.values.Command === consts.updateLogLevelId) {
				fieldName.formik.setFieldValue('ConfigSetting', JSON.stringify(parsed, null, 2));
			}
		}
	} catch (e) {
		error = error ?? { title: strings.Warning, content: strings.InvalidConfigMsg, level: 'warning' };
	} finally {
		if (error) {
			fieldName.formik.setFieldValue('file', '');
			showError(error.content || error.title, error.title, error.level || 'error');
		}
	}
};

const getCommandLookupId = (formikValues = {}, activeRecord = {}) => {
	const { combos = {} } = activeRecord || {};
	const { Command = [] } = combos;
	const isBiConfig =
		formikValues.Command === biConfigId &&
		activeRecord.data.equipmentType === consts.incubatorDeviceType;

	const commandNameToFind = isBiConfig
		? biConfigCommand
		: consts.updateConfigCommand;

	const item = Command.find(
		(item) => item && item.CommandName === commandNameToFind
	);

	return item ? item.LookupId : undefined;
}

const setTopicInputValidation = {
	field: 'Command',
	op: 'eq',
	value: ({ activeRecord }) => {
		const { combos = {} } = activeRecord || {};
		const { Command = [] } = combos;

		const item = Command.find(
			(item) => item && item.CommandName === 'settopic'
		);

		return item ? item.LookupId : false;
	}
}

const getCommandValue = ({ activeRecord }) => {
	const { combos = {} } = activeRecord || {};
	const { Command = [] } = combos;
	const item = Command.find((item) => item.CommandName === 'updatecertsreq');
	return item ? item.LookupId : false;
}

const accept = ".crt,.cert,.pem,.key,.pfx,.p12,.csr,.cer,.der,.jks,.p7b,.p7c,.json";

const model = new Model({
	name: 'Command',
	combos: [],
	skipSetBreadcrumbs: true,
	customizeSaveData: (toSave, { activeRecord, values }) => {
		const { combos = {} } = activeRecord || {};
		const { CommandServices: commandServices = [] } = combos;
		const logLevels = new Set();
		const { Services = [] } = values;
		const transformedServices = Services.reduce((acc, { Service, LogLevel }) => {
			const lookupId = Service;
			const serviceObject = commandServices.find((item) => item.LookupId === lookupId || item.DisplayValue === lookupId);
			if (!logLevels.has(LogLevel)) {
				logLevels.add(LogLevel);
				acc[consts.logLevels[LogLevel]] = [serviceObject.DisplayValue];
			} else {
				acc[consts.logLevels[LogLevel]].push(serviceObject.DisplayValue);
			}
			return acc;
		}, {});
		toSave.Services = transformedServices;
		return toSave;
	},
	formGroups: [
		{
			fields: ['Command'],
			columnSize: 12,
			controlled: true,
			fieldProps: {
				inlineLabelSize: 2
			}
		},
		{
			fields: ['Comment'],
			columnSize: 12,
			controlled: true,
			fieldProps: {
				inlineLabelSize: 2
			}
		},
		{
			fields: [
				'swucafile',
				'swucafilesig',
				'ksrootcafile',
				'ksrootcafilesig',
				'clientcert',
				'clientcertsig',
				'clientkey',
				'clientkeysig',
				'CATRootCA',
				'RMRootCA'
			],
			columnSize: 4,
			controlled: true,
			fieldProps: {
				inlineLabelSize: 6
			}
		},
		{
			fields: ['ConfigSetting'],
			columnSize: 12,
			controlled: true,
			fieldProps: {
				inlineLabelSize: 2
			}
		},
		{
			fields: ['Services'],
			columnSize: 12,

			fieldProps: {
				inlineLabelSize: 2
			}
		},
		{
			fields: ['file'],
			columnSize: 12,
			controlled: true,
			fieldProps: {
				inlineLabelSize: 2
			},
		},
		{
			fields: ['DataTopic', 'TelemetryTopic'],
			columnSize: 12,
			controlled: true,
			fieldProps: {
				inlineLabelSize: 2
			},
		},
	],
	columns: [
		{
			field: 'Command',
			header: 'Command',
			required: true,
			resets: ['ConfigSetting', 'file'],
			getOptionDisabled: (option) => option.disabled,
			onChange: ({value, formik, activeRecord}) => {
				let Services = !Array.isArray(formik.values.Services) ? [] : formik.values.Services;
				let shouldResetServices = Services.length > 0 && value !== consts.updateLogLevel;
				if (value === consts.updateLogLevel) {
					if (Services.length === 0) {
						Services = [
							{
								id: 1,
								LogLevel: consts.infoLevel,
								Service: consts.DefaultLogLevelService
							}
						];
						shouldResetServices = true;
					}
				} else if (Services.length > 0) {
					Services = [];
				}
				if (shouldResetServices) {
					formik.setFieldValue('Services', Services);
				}
				// for different commands we need to reset the config setting
				if(value !== consts.setTopicId) {
					formik.setFieldValue('DataTopic', []);
					formik.setFieldValue('TelemetryTopic', []);
				}
			},
			lookup: 'Command'
		},
		{
			field: 'swucafile',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept
		},
		{
			field: 'swucafilesig',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept
		},
		{
			field: 'ksrootcafile',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept
		},
		{
			field: 'ksrootcafilesig',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept
		},
		{
			field: 'clientcert',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			required: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept
		},
		{
			field: 'clientcertsig',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept
		},
		{
			field: 'clientkey',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			required: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept
		},
		{
			field: 'clientkeysig',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept
		},
		{
			field: 'Comment',
			rows: 2,
			multiline: true,
			maxLength: 500,
			required: true
		},
		{
			field: 'ConfigSetting',
			header: 'Config',
			rows: 10,
			multiline: true,
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: ({ formik, activeRecord }) => getCommandLookupId(formik.values, activeRecord)
				}
			],
			required: [
				{
					field: 'Command',
					op: 'eq',
					value: ({ formik = {}, activeRecord }) => getCommandLookupId(formik.values, activeRecord)
				}
			],
			readOnly: ({ formik, activeRecord }) =>
				!!(formik.values.Command === biConfigId && activeRecord.data.equipmentType === incubatorDeviceType)
		},
		{
			field: 'file',
			hideLabel: true,
			type: 'file',
			accept: 'application/json',
			onChange: onfileSelected,
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: ({ formik, activeRecord }) => getCommandLookupId(formik.values, activeRecord)
				}
			]
		},
		{
			field: 'DataTopic',
			header:'Datatopic',
			lookup: 'DataTopics',
			multiple: true,
			type: 'object',
			inputWhen: [ setTopicInputValidation ]
		},
		{
			field: 'TelemetryTopic',
			header:'Telemetrytopic',
			multiple: true,
			type: 'object',
			lookup: 'TDataTopics',
			inputWhen: [ setTopicInputValidation ]
		},
		{
			header: 'Services',
			field: 'Services',
			width: 220,
			hidden: true,
			required: [['Command', consts.updateLogLevel]],
			inputWhen: [['Command', consts.updateLogLevel]],
			multiple: true,
			customizeInput: ({ inputProps: { error } }) => (
				<CommandServicesDataGrid
					error={error}
				/>
			),
			validate: yup
				.array()
				.nullable()
				.of(
					yup.object().shape({
						LogLevel: yup.string().required(),
						Service: yup.string().required('Service is required').nullable(),
					})
				)
				.test({
					name: 'DuplicateServiceFound',
					// test: function ({parent: {values}}) {
					test: function (services = [], activeRecord) {
						const serviceNames = new Set();
						return !services.some((service) => {
							if (serviceNames.has(service.Service)) {
								return true;
							}
							serviceNames.add(service.Service);
							return false; // Explicitly return false if the condition is not met
						});
					},
					message: 'DuplicateServiceFound'
				})
				.test({
					name: 'AtLeastOneServiceisRequired',
					test: function (services = [], activeRecord) {
						if (activeRecord.parent.Command === consts.updateLogLevel) {
							return services.some((service) => service.Service !== '');
						}
						return true;
					},
					message: 'ServiceRequired'
				})
		},
		{
			field: 'RMRootCA',
			header:'rmRootCA',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept: '.crt'
		},
		{
			field: 'CATRootCA',
			header:'catRootCA',
			type: 'file',
			inputWhen: [
				{
					field: 'Command',
					op: 'eq',
					value: getCommandValue
				}
			],
			accept: '.crt'
		}
	]
});

export default model.createFormDialog();